/**
 * The push-notification destination table -- one entry per vendor.
 *
 * ONE table, deliberately. This replaced four parallel maps (wire name, label,
 * reverse lookup, select options) plus a fifth copy of the host rules in
 * `pushNotificationUrl.js`. Adding a vendor meant editing five places, and
 * missing one of them failed in a different way each time: an unlabelled row, a
 * vendor absent from the picker, or a URL the browser accepted and the server
 * then refused. Now it is one entry and the views below derive from it.
 *
 * Mirrors `shared_push_notification::vendor` and
 * `shared_push_notification::credential`. Nothing here is trusted: api-server
 * validates at the request boundary and the delivery service validates again
 * after decrypting. This exists so someone learns they pasted the wrong thing
 * before they click Save.
 */

/**
 * @typedef {object} PushField
 * @property {string} key The credential-document key the server requires.
 * @property {"destination_url"|"instance_label"|"splunk_cloud_host"|"secret"} kind
 *   What it holds, which is what `pushNotificationUrl.js` dispatches its
 *   validation on and what decides whether the input is masked.
 * @property {string} label What to call it on screen.
 * @property {string} hint Where to find the value.
 * @property {string} [placeholder]
 */

/**
 * @typedef {object} PushDestination
 * @property {"webhook"|"instance"|"powerAutomateWorkflow"|"splunkCloud"} kind
 * @property {string[]} [hosts] `webhook`: acceptable hosts, compared
 *   case-insensitively. A SET because a vendor's endpoints can be regional.
 *   Enumerated, never a suffix pattern -- a dot-suffix rule on a cloud
 *   vendor's apex would authorize every API it hosts.
 * @property {string} [pathPrefix] `webhook`: the path we pin. Whether it is
 *   required as a prefix or in full is `pathMatch`.
 * @property {"prefix"|"exact"} [pathMatch] `webhook`: `prefix` when the path
 *   continues past what we pin (a Slack webhook's tail IS the credential),
 *   `exact` when it names one API method and a longer path is a different call.
 *   Mirrors `PathRule` in `shared-push-notification`.
 * @property {string} [apex] `instance`: the pinned registrable domain the
 *   customer's label sits under. `powerAutomateWorkflow`: the pinned apex the
 *   two environment labels sit under; the path and query rules are in
 *   `pushNotificationUrl.js`, mirroring `Destination::PowerAutomateWorkflow`.
 *   `splunkCloud`: the pinned apex the stack label sits under, mirroring
 *   `Destination::SplunkCloudCollector`.
 * @property {string} [path] `instance` and `splunkCloud`: the exact path, which
 *   we build.
 */

/**
 * @typedef {object} PushVendor
 * @property {string} key Database/wire form, e.g. `"slack"`.
 * @property {string} wire The protobuf enum name the gateway uses.
 * @property {string} label What a person calls it.
 * @property {string} description One line, shown in the picker before a choice
 *   is made -- so it says what arrives there, not what the product is.
 * @property {number} minWindowSeconds Shortest batching window this endpoint
 *   can take. A destination sends at most one request per window and nothing
 *   rate limits behind it, so this is the endpoint's limit expressed as a
 *   window. Mirrors `Vendor::min_window_seconds`.
 * @property {string} [authScheme] The HTTP authorization scheme this vendor's
 *   key is presented under, when it has one. `Credential::parse` strips it on
 *   the way in, so a customer may paste the key either way; absent means the
 *   value is stored exactly as typed and nothing may be stripped from it.
 *   Mirrors `Vendor::auth_scheme`.
 * @property {PushDestination} destination
 * @property {PushField[]} fields The credential fields, in the order they are
 *   asked for. A list rather than one field: Slack needs a URL, a SIEM feed
 *   needs a URL and one or two keys, and a service-account route needs three.
 */

/** @type {PushVendor[]} */
export const PUSH_VENDORS = [
    {
        key: "slack",
        wire: "PUSH_NOTIFICATION_VENDOR_SLACK",
        label: "Slack",
        description: "Post a digest of each batch to a channel.",
        minWindowSeconds: 10,
        destination: {
            kind: "webhook",
            hosts: ["hooks.slack.com"],
            pathPrefix: "/services/",
            pathMatch: "prefix",
        },
        fields: [
            {
                key: "webhook_url",
                kind: "destination_url",
                label: "Slack webhook URL",
                hint: "Create an Incoming Webhook in Slack, then paste the URL here.",
                placeholder: "https://hooks.slack.com/services/...",
            },
        ],
    },
    {
        key: "microsoft_teams",
        wire: "PUSH_NOTIFICATION_VENDOR_MICROSOFT_TEAMS",
        label: "Microsoft Teams",
        description: "Post a card summarizing each batch to a channel.",
        minWindowSeconds: 60,
        destination: {
            kind: "powerAutomateWorkflow",
            apex: "environment.api.powerplatform.com",
        },
        fields: [
            {
                key: "webhook_url",
                kind: "destination_url",
                label: "Teams workflow URL",
                hint: 'In the Teams channel, open Workflows and add "Send webhook alerts to a channel", then paste the URL it gives you. Office 365 connector URLs no longer work.',
                placeholder: "https://....environment.api.powerplatform.com/powerautomate/...",
            },
        ],
    },
    {
        key: "splunk",
        wire: "PUSH_NOTIFICATION_VENDOR_SPLUNK",
        label: "Splunk Cloud",
        description: "Send one event per detection or finding to an index.",
        minWindowSeconds: 10,
        authScheme: "Splunk",
        destination: {
            kind: "splunkCloud",
            apex: "splunkcloud.com",
            path: "/services/collector/event",
        },
        fields: [
            {
                key: "host",
                kind: "splunk_cloud_host",
                label: "HEC host",
                hint: "Your Splunk Cloud HEC host, e.g. http-inputs-<stack>.splunkcloud.com",
                placeholder: "http-inputs-acme.splunkcloud.com",
            },
            {
                key: "api_key",
                kind: "secret",
                label: "HEC token",
                hint: "In Splunk: Settings, Data inputs, HTTP Event Collector.",
            },
        ],
    },
];

const BY_KEY = Object.fromEntries(PUSH_VENDORS.map((v) => [v.key, v]));
const BY_WIRE = Object.fromEntries(PUSH_VENDORS.map((v) => [v.wire, v]));

/** The vendor for a database/wire key, or undefined. */
export function vendorByKey(key) {
    return BY_KEY[key];
}

/**
 * The vendor for a protobuf enum name, or undefined.
 *
 * Undefined for a value this build does not know, which happens when api-server
 * has rolled ahead. The caller shows "Unknown" rather than guessing -- labelling
 * an unrecognized destination as Slack would be worse than saying nothing.
 */
export function vendorByWire(wire) {
    return BY_WIRE[wire];
}

/**
 * The batching windows a vendor's endpoint can take, shortest first.
 *
 * Derived from `minWindowSeconds` rather than listed, so a vendor whose endpoint
 * needs a longer window cannot be offered one the server will refuse. There is
 * no "as they happen": one request per detection is the only setting that can
 * outrun an endpoint's rate limit, and nothing limits behind the window.
 *
 * The empty value sends no field at all, so the destination inherits the service
 * default rather than pinning a number that then cannot follow a change to it.
 * Deliberately no explicit 60 alongside it: on screen the two would be
 * indistinguishable. The upper end mirrors api-server's `MAX_WINDOW_SECONDS`.
 *
 * Empty for a vendor this build does not know, the same way `vendorByWire`
 * returns undefined: without its limit there is no window we can say is safe,
 * and offering all of them would be a guess in the unsafe direction.
 */
export function windowOptions(vendorKey) {
    const vendor = BY_KEY[vendorKey];
    if (!vendor) return [];
    const floor = vendor.minWindowSeconds;
    return [
        { value: "10", label: "Every 10 seconds", seconds: 10 },
        { value: "", label: "Every minute", seconds: 60 },
        { value: "300", label: "Every 5 minutes", seconds: 300 },
        { value: "900", label: "Every 15 minutes", seconds: 900 },
    ]
        .filter((option) => option.seconds >= floor)
        .map(({ value, label }) => ({ value, label }));
}
